import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { RichTextEditorComponent, Toolbar, Image, Link, HtmlEditor, QuickToolbar, PasteCleanup, Inject } from '@syncfusion/ej2-react-richtexteditor';
import { messageService, agentService } from '../services/mailboxService';
import { useAccounts } from '../context/AccountContext';
import { safeHtml } from '../utils/sanitizeHtml';

function ComposePage() {
  const navigate = useNavigate();
  const { messageId } = useParams();
  const location = useLocation();
  const editorRef = useRef(null);
  const { activeMailboxes, isMultiAccount, mailboxes } = useAccounts();
  
  const [fromMailboxId, setFromMailboxId] = useState('');
  const [showFromPicker, setShowFromPicker] = useState(false);
  const [to, setTo] = useState('');
  const [cc, setCc] = useState('');
  const [bcc, setBcc] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState(location.state?.body || '');
  const [sending, setSending] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [aiSuggestion, setAiSuggestion] = useState('');
  const [showAiPanel, setShowAiPanel] = useState(false);
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);

  // When composing with multi-account: show the picker modal on mount
  useEffect(() => {
    if (isMultiAccount && !fromMailboxId && !messageId) {
      setShowFromPicker(true);
    } else if (mailboxes.length === 1 && !fromMailboxId) {
      setFromMailboxId(mailboxes[0].mailboxId || mailboxes[0].mailboxID || mailboxes[0].id);
    }
  }, [isMultiAccount, mailboxes, fromMailboxId, messageId]);

  useEffect(() => {
    if (messageId) {
      loadReplyMessage();
    }
  }, [messageId]);

  const loadReplyMessage = async () => {
    try {
      const message = await messageService.getMessage(messageId);
      setReplyTo(message);
      setTo(message.from);
      setSubject(`Re: ${message.subject?.replace(/^Re:\s*/i, '') || ''}`);
      setBody(`<br><br><div style="border-left: 2px solid #ccc; padding-left: 12px; margin-left: 0; color: #666;">
        <p>On ${new Date(message.receivedUtc).toLocaleString()}, ${message.fromName || message.from} wrote:</p>
        ${message.bodyHtml || message.bodyText?.replace(/\n/g, '<br>') || ''}
      </div>`);
    } catch (error) {
      console.error('Failed to load reply message:', error);
    }
  };

  const handleSend = async () => {
    if (!to.trim()) {
      alert('Please enter a recipient');
      return;
    }

    if (isMultiAccount && !fromMailboxId) {
      alert('Please select which account to send from');
      return;
    }

    setSending(true);
    try {
      const editorContent = editorRef.current?.value || body;
      
      await messageService.sendMessage({
        to: to.split(',').map(e => e.trim()),
        cc: cc ? cc.split(',').map(e => e.trim()) : [],
        bcc: bcc ? bcc.split(',').map(e => e.trim()) : [],
        subject,
        bodyHtml: editorContent,
        inReplyTo: replyTo?.serverMessageId,
        mailboxId: fromMailboxId || undefined,
      });
      
      navigate('/inbox');
    } catch (error) {
      console.error('[Webmail] Failed to send message:', error);
      alert('Failed to send message. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleSaveDraft = async () => {
    try {
      const editorContent = editorRef.current?.value || body;
      
      await messageService.sendMessage({
        to: to.split(',').map(e => e.trim()),
        cc: cc ? cc.split(',').map(e => e.trim()) : [],
        bcc: bcc ? bcc.split(',').map(e => e.trim()) : [],
        subject,
        bodyHtml: editorContent,
        isDraft: true
      });
      
      navigate('/inbox/drafts');
    } catch (error) {
      console.error('Failed to save draft:', error);
    }
  };

  const handleAiGenerate = async () => {
    if (!aiPrompt.trim()) return;
    
    setAiLoading(true);
    try {
      const response = await agentService.generateDraft(aiPrompt, messageId);
      setAiSuggestion(response.draft || '');
    } catch (error) {
      console.error('Failed to generate AI draft:', error);
      setAiSuggestion('Failed to generate suggestion. Please try again.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleInsertAiSuggestion = () => {
    if (aiSuggestion && editorRef.current) {
      editorRef.current.executeCommand('insertHTML', aiSuggestion);
      setAiSuggestion('');
      setAiPrompt('');
      setShowAiPanel(false);
    }
  };

  const handleRewrite = async (tone) => {
    const editorContent = editorRef.current?.value;
    if (!editorContent) return;
    
    setAiLoading(true);
    try {
      const response = await agentService.rewrite(editorContent, tone);
      if (response.rewritten && editorRef.current) {
        editorRef.current.value = response.rewritten;
      }
    } catch (error) {
      console.error('Failed to rewrite:', error);
    } finally {
      setAiLoading(false);
    }
  };

  const toolbarSettings = {
    items: [
      'Bold', 'Italic', 'Underline', 'StrikeThrough', '|',
      'FontName', 'FontSize', 'FontColor', 'BackgroundColor', '|',
      'Formats', 'Alignments', '|',
      'OrderedList', 'UnorderedList', '|',
      'Outdent', 'Indent', '|',
      'CreateLink', 'Image', '|',
      'ClearFormat', 'SourceCode', '|',
      'Undo', 'Redo'
    ]
  };

  // Keep paste NEUTRAL: copying a template (or any text from a dark-themed
  // pane) brings the source's `color` / `background-color` along and leaks it
  // into the outgoing mail. This is the Syncfusion default allowedStyleProps
  // whitelist MINUS the three colour properties, so formatting (bold, lists,
  // size, alignment) survives but foreground/background colour is dropped.
  // Deliberate colour via the toolbar buttons still works.
  const pasteCleanupSettings = {
    prompt: false,
    plainText: false,
    keepFormat: true,
    allowedStyleProps: [
      'border', 'border-bottom', 'border-left', 'border-radius', 'border-right',
      'border-style', 'border-top', 'border-width', 'clear', 'cursor', 'direction',
      'display', 'float', 'font', 'font-family', 'font-size', 'font-weight',
      'font-style', 'height', 'left', 'line-height', 'list-style-type', 'list-style',
      'margin', 'margin-top', 'margin-left', 'margin-right', 'margin-bottom',
      'max-height', 'max-width', 'min-height', 'min-width', 'overflow', 'padding',
      'padding-bottom', 'padding-left', 'padding-right', 'padding-top', 'position',
      'right', 'table-layout', 'text-align', 'text-decoration', 'text-indent',
      'top', 'vertical-align', 'visibility', 'white-space', 'width',
    ],
  };

  const composeForm = (
    <div style={{ display: 'flex', flex: 1, overflow: 'hidden', height: '100%', minWidth: 0 }}>
      {/* Compose Form */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '20px', minHeight: 0, minWidth: 0, overflow: 'auto' }}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 600 }}>
            {messageId ? 'Reply' : 'New Message'}
          </h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button 
              className="btn btn-secondary" 
              onClick={() => navigate(-1)}
            >
              Cancel
            </button>
            <button 
              className="btn btn-secondary" 
              onClick={handleSaveDraft}
            >
              Save Draft
            </button>
            <button 
              className="btn btn-primary" 
              onClick={handleSend}
              disabled={sending}
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>

        {/* Form Fields */}
        <div style={{ marginBottom: '16px' }}>
          {/* From display — always show which account will send */}
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ width: '60px', color: 'var(--text-muted)' }}>From:</label>
              {isMultiAccount ? (
                <select
                  className="form-input"
                  value={fromMailboxId}
                  onChange={(e) => setFromMailboxId(e.target.value)}
                  style={{ cursor: 'pointer' }}
                >
                  <option value="">-- Select account --</option>
                  {mailboxes.map((mb) => {
                    const mbId = mb.mailboxId || mb.mailboxID || mb.id;
                    const email = mb.emailAddress || mb.email;
                    return (
                      <option key={mbId} value={mbId}>{email}</option>
                    );
                  })}
                </select>
              ) : (
                <input
                  type="text"
                  className="form-input"
                  value={mailboxes[0]?.emailAddress || mailboxes[0]?.email || ''}
                  disabled
                  style={{ opacity: 0.7 }}
                />
              )}
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ width: '60px', color: 'var(--text-muted)' }}>To:</label>
              <input
                type="email"
                className="form-input"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                placeholder="recipient@example.com"
                autoComplete="off"
                name="to-recipient"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ width: '60px', color: 'var(--text-muted)' }}>Cc:</label>
              <input
                type="email"
                className="form-input"
                value={cc}
                onChange={(e) => setCc(e.target.value)}
                placeholder="cc@example.com"
                autoComplete="off"
                name="cc-recipient"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ width: '60px', color: 'var(--text-muted)' }}>Bcc:</label>
              <input
                type="email"
                className="form-input"
                value={bcc}
                onChange={(e) => setBcc(e.target.value)}
                placeholder="bcc@example.com (hidden from other recipients)"
                autoComplete="off"
                name="bcc-recipient"
              />
            </div>
          </div>
          <div className="form-group" style={{ marginBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <label style={{ width: '60px', color: 'var(--text-muted)' }}>Subject:</label>
              <input
                type="text"
                className="form-input"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Email subject"
              />
            </div>
          </div>
        </div>

        {/* AI Quick Actions — hidden until the AI rewrite pipeline is wired
            up against the platform LLM provider. State variables stay so we
            can re-enable in one line if/when that's done.
            (showAiPanel, aiPrompt, aiSuggestion, handleAiGenerate,
             handleInsertAiSuggestion, handleRewrite) */}
        {false && (
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button className="btn btn-secondary" onClick={() => setShowAiPanel(!showAiPanel)} style={{ fontSize: '13px' }}>✨ AI Assist</button>
            <button className="btn btn-secondary" onClick={() => handleRewrite('professional')} style={{ fontSize: '13px' }} disabled={aiLoading}>Make Professional</button>
            <button className="btn btn-secondary" onClick={() => handleRewrite('concise')} style={{ fontSize: '13px' }} disabled={aiLoading}>Make Concise</button>
            <button className="btn btn-secondary" onClick={() => handleRewrite('friendly')} style={{ fontSize: '13px' }} disabled={aiLoading}>Make Friendly</button>
          </div>
        )}

        {/* Rich Text Editor */}
        <div style={{ flex: 1, minHeight: 0 }}>
          <RichTextEditorComponent
            ref={editorRef}
            height="100%"
            toolbarSettings={toolbarSettings}
            // Embed inline images as base64 data URIs so the server-side
            // ExtractInlineDataImages can convert them into `cid:` MIME
            // LinkedResources. Default is "Blob" which produces local-only
            // `blob:` URLs that recipients see as broken images.
            insertImageSettings={{ saveFormat: 'Base64' }}
            pasteCleanupSettings={pasteCleanupSettings}
            value={body}
            placeholder="Write your message..."
          >
            <Inject services={[Toolbar, Image, Link, HtmlEditor, QuickToolbar, PasteCleanup]} />
          </RichTextEditorComponent>
        </div>
      </div>

      {/* AI Panel — hidden alongside the AI Quick Actions above. */}
      {false && showAiPanel && (
        <div className="ai-panel">
          <div className="ai-panel-header">
            <div className="ai-panel-icon">✨</div>
            <span className="ai-panel-title">AI Writing Assistant</span>
          </div>

          <div style={{ padding: '16px' }}>
            <div className="form-group" style={{ marginBottom: '12px' }}>
              <label className="form-label" style={{ marginBottom: '8px', display: 'block' }}>
                What would you like to write?
              </label>
              <textarea
                className="form-input"
                value={aiPrompt}
                onChange={(e) => setAiPrompt(e.target.value)}
                placeholder="E.g., 'Write a follow-up email about the project deadline' or 'Politely decline a meeting request'"
                rows={5}
                style={{ 
                  resize: 'none', 
                  width: '100%', 
                  boxSizing: 'border-box',
                  lineHeight: '1.5'
                }}
              />
            </div>
            <button 
              className="btn btn-primary" 
              onClick={handleAiGenerate}
              disabled={aiLoading || !aiPrompt.trim()}
              style={{ width: '100%', marginTop: '8px' }}
            >
              {aiLoading ? 'Generating...' : 'Generate Draft'}
            </button>
          </div>

          {aiSuggestion && (
            <div style={{ padding: '16px', borderTop: '1px solid var(--border-color)' }}>
              <div style={{ fontSize: '12px', fontWeight: 600, color: 'var(--text-muted)', marginBottom: '8px' }}>
                AI SUGGESTION
              </div>
              <div style={{ 
                background: 'var(--card-bg)', 
                padding: '12px', 
                borderRadius: '8px',
                fontSize: '14px',
                lineHeight: 1.6,
                marginBottom: '12px',
                maxHeight: '300px',
                overflow: 'auto'
              }}>
                <div dangerouslySetInnerHTML={safeHtml(aiSuggestion.replace(/\n/g, '<br>'))} />
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button 
                  className="btn btn-primary" 
                  onClick={handleInsertAiSuggestion}
                  style={{ flex: 1 }}
                >
                  Insert
                </button>
                <button 
                  className="btn btn-secondary" 
                  onClick={() => setAiSuggestion('')}
                >
                  Discard
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );

  // Render modal + main content
  return (
    <>
      {/* From account picker modal — shown when composing with multi-account */}
      {showFromPicker && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.6)', zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            background: 'var(--card-bg, #1e293b)', borderRadius: 12,
            padding: '24px', minWidth: 320, maxWidth: 400,
            border: '1px solid var(--border-color)',
            boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
          }}>
            <h3 style={{ margin: '0 0 16px', fontSize: 16 }}>Send from which account?</h3>
            {mailboxes.map((mb) => {
              const mbId = mb.mailboxId || mb.mailboxID || mb.id;
              const email = mb.emailAddress || mb.email;
              return (
                <button
                  key={mbId}
                  onClick={() => { setFromMailboxId(mbId); setShowFromPicker(false); }}
                  style={{
                    display: 'block', width: '100%', padding: '12px 16px',
                    marginBottom: 8, background: 'var(--bg-primary, #0f172a)',
                    border: '1px solid var(--border-color)', borderRadius: 8,
                    color: 'var(--text-primary)', cursor: 'pointer', fontSize: 14,
                    textAlign: 'left',
                  }}
                >
                  {email}
                </button>
              );
            })}
            <button
              onClick={() => { navigate(-1); }}
              style={{
                marginTop: 8, background: 'none', border: 'none',
                color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13,
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {composeForm}
    </>
  );
}

export default ComposePage;

